import "./SotGa4Labeling.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import cn from "classnames";
import { Brand60 } from "../utils/colors";
import { RouteComponentProps } from "@reach/router";
import {
  Button,
  ButtonType,
  FilterBar,
  FullPageSpinner,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "../Components";
import { useStateFunction } from "../utils/hooks/useData";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import { MdDelete, MdErrorOutline, MdOutlineFileDownload, MdSave, MdSend } from "react-icons/md";
import { ButtonFrameworkVariant } from "../Components/ButtonFramework";
import { Mixpanel, MxE } from "../utils/mixpanelWrapper";
import { formatNumber } from "../utils/format-utils";
import { downloadJSONToCSV } from "../utils/download-utils";
import { CustomSegmentsData, SotGa4MappingRow } from "./SotGa4MappingTool";
import SotGa4LabelingTable from "./SotGa4LabelingTable";
import SotGa4PendingSegmentationChanges from "./SotGa4PendingSegmentationChanges";
import { dbSotGa4 } from "../utils/db";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { Nav } from "react-bootstrap";

export interface SotGa4Row {
  ad_group_id?: string;
  ad_group_name?: string;
  ad_id?: string;
  ad_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  source_of_truth?: string;
  channel?: string;
  platform?: string;
  raw_medium?: string;
  raw_source?: string;
  edits?: SotGa4RowEdit;
  campaign_key: string;
}

export interface SotGa4RowEdit {
  channel?: { label: string; value: string };
  platform?: { label: string; value: string };
  [key: string]: { label: string; value: string } | undefined;
}

export interface SotGa4EditsMap {
  [campaign_key: string]: SotGa4Row;
}

interface LabelingProps {
  data: SotGa4MappingRow[] | null;
  customSegments: CustomSegmentsData[] | undefined;
  fetchRawData: () => void;
}

enum TabOptions {
  LABELED = "Labeled",
  UNLABELED = "Unlabeled",
}

export interface SotGa4EditsMapAction {
  type:
    | "UPDATE_SEGMENT_EDIT"
    | "DELETE_SEGMENT_EDIT"
    | "SET_STATE_FROM_CACHE"
    | "SET_MAP"
    | "RESET";
  cachedState?: SotGa4EditsMap;
  editsMap?: SotGa4EditsMap;
  updates?: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[];
  campaign_keys?: string[];
  segmentName?: string;
}

const SotGa4Labeling = ({
  data,
  fetchRawData,
  customSegments,
}: LabelingProps & RouteComponentProps): JSX.Element => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const [labeledEditsMap, setLabeledEditsMap] = useState<SotGa4EditsMap>({});

  const [unlabeledEditsMap, setUnlabeledEditsMap] = useState<SotGa4EditsMap>({});
  const [selectedRows, setSelectedRows] = useState<Record<string, SotGa4Row>>({});
  const [activeTab, setActiveTab] = useState<string>(TabOptions.UNLABELED);

  const [refreshing, setRefreshing] = useState(false);

  const [dialog, setDialog] = useState<{
    show: boolean;
    title: string;
    body: string;
    variant?: AlertDialogVariants;
  } | null>(null);

  const loadDataFromDexie = useCallback(async () => {
    try {
      const [labeledRecords, unlabeledRecords] = await Promise.all([
        dbSotGa4.editRecords
          .where("cid")
          .equals(cid)
          .and(record => record.batchType === "labeled")
          .toArray(),
        dbSotGa4.editRecords
          .where("cid")
          .equals(cid)
          .and(record => record.batchType === "unlabeled")
          .toArray(),
      ]);

      const labeledMap: SotGa4EditsMap = {};
      const unlabeledMap: SotGa4EditsMap = {};

      labeledRecords.forEach(record => {
        labeledMap[record.campaignKey] = record.rowData;
      });

      unlabeledRecords.forEach(record => {
        unlabeledMap[record.campaignKey] = record.rowData;
      });

      setLabeledEditsMap(labeledMap);
      setUnlabeledEditsMap(unlabeledMap);
    } catch (error) {
      console.error("Error loading data from Dexie:", error);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) {
      return;
    }

    loadDataFromDexie();
  }, [cid, loadDataFromDexie]);

  const updateEdits = useCallback(
    async (
      updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[],
      batchType: "labeled" | "unlabeled"
    ) => {
      if (!cid) {
        return;
      }

      const currentEditsMap = batchType === "labeled" ? labeledEditsMap : unlabeledEditsMap;
      const setEditsMap = batchType === "labeled" ? setLabeledEditsMap : setUnlabeledEditsMap;
      const newEditsMap = { ...currentEditsMap };

      for (const update of updates) {
        const { rows, edits } = update;
        if (!rows) {
          throw new Error("No row provided for updating segment edit operation.");
        }

        rows.forEach(row => {
          const existingRow = newEditsMap[row.campaign_key];

          if (!existingRow) {
            newEditsMap[row.campaign_key] = { ...row, edits: { ...edits } };
          } else {
            const updatedEdits = { ...existingRow.edits, ...edits };

            for (const segmentName of Object.keys(updatedEdits)) {
              const segmentEdit = updatedEdits[segmentName];
              if (segmentEdit?.label === row[segmentName]) {
                delete updatedEdits[segmentName];
              }
            }

            newEditsMap[row.campaign_key] = { ...existingRow, edits: updatedEdits };

            const rowEdits = R.pathOr({}, [row.campaign_key, "edits"], newEditsMap);

            if (Object.keys(rowEdits).length === 0) {
              delete newEditsMap[row.campaign_key];
            }
          }
        });
      }

      setEditsMap(prevEditsMap => {
        return { ...prevEditsMap, ...newEditsMap };
      });

      for (const [campaignKey, rowData] of Object.entries({ ...currentEditsMap, ...newEditsMap })) {
        await dbSotGa4.editRecords.add({
          cid,
          campaignKey,
          batchType,
          rowData: rowData as SotGa4MappingRow,
          timestamp: Date.now(),
        });
      }
    },
    [cid, labeledEditsMap, unlabeledEditsMap]
  );

  const updateUnlabeledEdits = useCallback(
    (updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[]) => {
      return updateEdits(updates, "unlabeled");
    },
    [updateEdits]
  );

  const updateLabeledEdits = useCallback(
    (updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[]) => {
      return updateEdits(updates, "labeled");
    },
    [updateEdits]
  );

  const deleteSegmentEdit = useCallback(
    async (campaign_keys: string[], segmentName: string, batchType: "labeled" | "unlabeled") => {
      if (!cid || !campaign_keys || !segmentName) {
        return;
      }

      const currentEditsMap = batchType === "labeled" ? labeledEditsMap : unlabeledEditsMap;
      const newEditsMap = R.clone(currentEditsMap);

      campaign_keys.forEach(campaign_key => {
        const updatedRow = newEditsMap[campaign_key];
        const updatedRowEdits = updatedRow.edits ?? {};
        delete updatedRowEdits[segmentName];

        if (Object.keys(updatedRowEdits).length === 0) {
          delete newEditsMap[campaign_key];
        }
      });

      if (batchType === "labeled") {
        setLabeledEditsMap(newEditsMap);
      } else {
        setUnlabeledEditsMap(newEditsMap);
      }

      await dbSotGa4.editRecords
        .where("cid")
        .equals(cid)
        .and(record => record.batchType === batchType)
        .delete();

      const editRecords = Object.entries(newEditsMap).map(([campaignKey, rowData]) => ({
        cid,
        campaignKey,
        batchType,
        rowData: rowData as SotGa4MappingRow,
        timestamp: Date.now(),
      }));

      if (editRecords.length > 0) {
        await dbSotGa4.editRecords.bulkAdd(editRecords);
      }
    },
    [cid, labeledEditsMap, unlabeledEditsMap]
  );

  const resetEdits = useCallback(
    async (batchType: "labeled" | "unlabeled") => {
      if (!cid) {
        return;
      }

      if (batchType === "labeled") {
        setLabeledEditsMap({});
      } else {
        setUnlabeledEditsMap({});
      }

      await dbSotGa4.editRecords
        .where("cid")
        .equals(cid)
        .and(record => record.batchType === batchType)
        .delete();
    },
    [cid]
  );

  const filterBarOptions = useMemo(() => {
    const options = [
      { name: "ad_group_id", label: "Ad Group ID" },
      { name: "ad_group_name", label: "Ad Group" },
      { name: "ad_id", label: "Ad ID" },
      { name: "ad_name", label: "Ad Name" },
      { name: "campaign_id", label: "Campaign ID" },
      { name: "campaign_name", label: "Campaign" },
      { name: "source_of_truth", label: "Source of Truth" },
      { name: "channel", label: "Channel" },
      { name: "platform", label: "Platform" },
      { name: "raw_medium", label: "Medium" },
      { name: "raw_source", label: "Source" },
    ];
    return options;
  }, []);

  const unlabeledData = useMemo(() => {
    return data?.filter(row => !(row.pg_seg_id || row.ingested_seg_id));
  }, [data]);

  const labeledData = useMemo(() => {
    return data?.filter(row => row.pg_seg_id || row.ingested_seg_id);
  }, [data]);

  const incompleteEditsMap = useMemo(() => {
    if (!customSegments) {
      return {};
    }

    const unlabeledEditsMapCopy = R.clone(unlabeledEditsMap);
    const segmentKeys = Object.keys(unlabeledEditsMap);
    const segmentNames = customSegments.map(segment => segment.segmentName);

    for (const key of segmentKeys) {
      const value = unlabeledEditsMapCopy[key];

      let hasAllSegments = true;
      for (const segmentName of segmentNames) {
        hasAllSegments =
          hasAllSegments && value.edits && (value.edits[segmentName] ?? value[segmentName]);
      }

      if (hasAllSegments) {
        delete unlabeledEditsMapCopy[key];
      }
    }

    return unlabeledEditsMapCopy;
  }, [customSegments, unlabeledEditsMap]);

  const completeUnlabeledEditsMap = useMemo(() => {
    if (!customSegments) {
      return {};
    }

    const unlabeledEditsMapCopy = R.clone(unlabeledEditsMap);
    const segmentKeys = Object.keys(unlabeledEditsMap);
    const segmentNames = customSegments.map(segment => segment.segmentName);

    for (const key of segmentKeys) {
      const value = unlabeledEditsMapCopy[key];

      let hasAllSegments = true;
      for (const segmentName of segmentNames) {
        hasAllSegments =
          hasAllSegments && value.edits && (value.edits[segmentName] ?? value[segmentName]);
      }

      if (!hasAllSegments) {
        delete unlabeledEditsMapCopy[key];
      }
    }

    return unlabeledEditsMapCopy;
  }, [customSegments, unlabeledEditsMap]);

  const dataToUse = useMemo(() => {
    return activeTab === TabOptions.UNLABELED
      ? {
          data: unlabeledData,
          editsMap: unlabeledEditsMap,
          updateEdits: updateUnlabeledEdits,
        }
      : { data: labeledData, editsMap: labeledEditsMap, updateEdits: updateLabeledEdits };
  }, [
    labeledData,
    labeledEditsMap,
    activeTab,
    unlabeledData,
    unlabeledEditsMap,
    updateLabeledEdits,
    updateUnlabeledEdits,
  ]);

  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);

  const filteredData = useMemo(() => {
    return dataToUse.data?.filter(filter) || [];
  }, [filter, dataToUse]);

  const exportData = useCallback(() => {
    const date = new Date();
    const csvData = R.map(row => unlabeledEditsMap[row.campaign_key] ?? row, filteredData).map(
      row => {
        const cleanedRow = {
          ...R.mapObjIndexed(v => (v === null ? "" : v), row),
        };
        return cleanedRow;
      }
    );

    downloadJSONToCSV(csvData, `SOTGA4Labeling ${cid} unlabeled ${date.toUTCString()}`);
  }, [filteredData, unlabeledEditsMap, cid]);

  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const hasPendingChanges = useMemo(() => {
    return !R.isEmpty(unlabeledEditsMap);
  }, [unlabeledEditsMap]);

  const pendingChangesCount = useMemo(() => {
    return Object.keys(unlabeledEditsMap).length;
  }, [unlabeledEditsMap]);

  const clearAllChanges = async () => {
    if (cid) {
      try {
        setDialog({
          show: true,
          title: "Do not close this window",
          body: "Wait a few seconds. Your changes are being saved.",
          variant: AlertDialogVariants.WARNING,
        });
        await resetEdits("labeled");
        await resetEdits("unlabeled");
        setDialog({
          show: true,
          title: "Changes saved",
          body: "Your changes have been saved.",
          variant: AlertDialogVariants.SUCCESS,
        });
      } catch (e: any) {
        setError({
          message: `Failed clearing edits from DexieDB for ${cid}. Error: ${e.message}`,
          reportError: e,
        });
      }
    }

    setShowPendingChanges(false);
  };

  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);

    try {
      // To Do: Implement save functionality

      await new Promise(resolve => setTimeout(resolve, 1000));

      await fetchRawData();
      setSelectedRows({});

      // Delete the previously pending changes from Dexie
      await resetEdits("labeled");

      // Refetch the data from Dexie
      await loadDataFromDexie();

      setSaving(false);
    } catch (e) {
      setSaving(false);
      const reportError = e as Error;
      setError({ message: reportError.message, reportError });
    }
  }, [fetchRawData, resetEdits, loadDataFromDexie, setError]);

  const refreshCrossChannel = useCallback(() => {
    (async () => {
      try {
        setRefreshing(true);
        Mixpanel.track(MxE.REFRESH_CC_VIEWS);

        // TODO: Implement cross-channel refresh
        await new Promise(resolve => setTimeout(resolve, 1000));

        setRefreshing(false);
        setError({
          variant: "success",
          title: "Publish started",
          message: <div>Cross-channel views refresh initiated</div>,
        });
      } catch (e: any) {
        setRefreshing(false);
        setError({
          message: `Failed to publish to dashboards for ${cid}. Error: ${e.message}`,
          reportError: e,
        });
      }
    })();
  }, [cid, setError]);

  return (
    <div className="sotGa4Labeling">
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const valueTab = value as keyof typeof value;

          if (!valueTab || valueTab === activeTab) {
            return;
          }

          setActiveTab(valueTab);
          setSelectedRows({});
        }}
      >
        <Nav.Item key={TabOptions.UNLABELED}>
          <Nav.Link eventKey={TabOptions.UNLABELED}>{TabOptions.UNLABELED}</Nav.Link>
        </Nav.Item>
        <Nav.Item key={TabOptions.LABELED}>
          <Nav.Link eventKey={TabOptions.LABELED}>{TabOptions.LABELED}</Nav.Link>
        </Nav.Item>
      </Nav>
      <div className="sotGa4LabelingBody">
        {dataToUse.data ? (
          <>
            <div className="filterBarContainer">
              <FilterBar
                options={filterBarOptions}
                lines={dataToUse.data || []}
                onFilter={setFilter}
                hasAdvanced
              />
              <div className="pendingChangesControls">
                <OverlayTrigger
                  placement={OverlayTrigger.PLACEMENTS.TOP.RIGHT}
                  delay={500}
                  overlay={
                    <Tooltip>
                      {hasPendingChanges
                        ? "Please save any labeling changes before publishing to other parts of the app."
                        : "Publish the latest mapping changes to dashboards now.\nChanges will auto-update by the next day if not manually published."}
                    </Tooltip>
                  }
                >
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                    icon={<MdSend color={Brand60} />}
                    onClick={() => refreshCrossChannel()}
                    disabled={hasPendingChanges}
                  >
                    {refreshing ? <Spinner /> : "Publish to dashboards"}
                  </Button>
                </OverlayTrigger>
                <Button
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  icon={<MdOutlineFileDownload />}
                  onClick={exportData}
                />
              </div>
            </div>

            <SotGa4LabelingTable
              data={filteredData}
              customSegments={customSegments}
              editsMap={dataToUse.editsMap}
              selectedRows={selectedRows}
              dispatchEditsMap={action => {
                if (action.type === "UPDATE_SEGMENT_EDIT") {
                  dataToUse.updateEdits(action.updates || []);
                } else if (action.type === "DELETE_SEGMENT_EDIT") {
                  deleteSegmentEdit(
                    action.campaign_keys || [],
                    action.segmentName || "",
                    activeTab === TabOptions.LABELED ? "labeled" : "unlabeled"
                  );
                } else if (action.type === "RESET") {
                  resetEdits(activeTab === TabOptions.LABELED ? "labeled" : "unlabeled");
                }
              }}
              setSelectedRows={setSelectedRows}
            />
          </>
        ) : (
          <FullPageSpinner />
        )}

        <div className="sotGa4LabelingControls">
          <div className="pendingChangesControls">
            <div className="unsavedChanges">
              <span className="unlabeledCounter">
                <span className={cn({ unsavedCount: hasPendingChanges })}>
                  {formatNumber(pendingChangesCount)}
                </span>
                /{formatNumber(filteredData?.length ?? 0)} Rows with Changes
              </span>
              {hasPendingChanges && (
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  design="secondary"
                  className="danger"
                  icon={<MdErrorOutline />}
                  onClick={() => {
                    setShowPendingChanges(true);
                  }}
                >
                  See {pendingChangesCount} Unsaved Change{pendingChangesCount > 1 ? "s" : ""}
                </Button>
              )}
            </div>
            {hasPendingChanges && (
              <>
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  onClick={clearAllChanges}
                  disabled={!hasPendingChanges}
                  icon={<MdDelete color={Brand60} />}
                >
                  Discard Changes
                </Button>
                <Button
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  onClick={save}
                  disabled={saving || !hasPendingChanges}
                  icon={<MdSave />}
                >
                  {saving ? <Spinner /> : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>
        {showPendingChanges && hasPendingChanges && (
          <SotGa4PendingSegmentationChanges
            incompleteUnlabeledEditsMap={incompleteEditsMap}
            completeUnlabeledEditsMap={completeUnlabeledEditsMap}
            labeledEditsMap={labeledEditsMap}
            dispatchUnlabeledEditsMap={action => {
              if (action.type === "UPDATE_SEGMENT_EDIT") {
                updateUnlabeledEdits(action.updates || []);
              } else if (action.type === "DELETE_SEGMENT_EDIT") {
                deleteSegmentEdit(
                  action.campaign_keys || [],
                  action.segmentName || "",
                  "unlabeled"
                );
              } else if (action.type === "RESET") {
                resetEdits("unlabeled");
              }
            }}
            dispatchLabeledEditsMap={action => {
              if (action.type === "UPDATE_SEGMENT_EDIT") {
                updateLabeledEdits(action.updates || []);
              } else if (action.type === "DELETE_SEGMENT_EDIT") {
                deleteSegmentEdit(action.campaign_keys || [], action.segmentName || "", "labeled");
              } else if (action.type === "RESET") {
                resetEdits("labeled");
              }
            }}
            setShowPendingChanges={setShowPendingChanges}
            customSegments={customSegments}
          />
        )}
      </div>
      {dialog && (
        <AlertDialog
          show={dialog.show}
          onHide={() => setDialog(null)}
          icon={null}
          title={dialog.title}
          body={dialog.body}
          primaryButtonLabel="Close"
          variant={dialog.variant}
        />
      )}
    </div>
  );
};

export default SotGa4Labeling;
